import { Component, EventEmitter, Input, OnDestroy, OnInit } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { LayoutBreakpointService } from '@headpower/layout';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { GroupPriority, PortalProduct } from 'src/app/modules/core/services/portal/portal-product';
import { PortalService } from 'src/app/modules/core/services/portal/portal.service';
import { ChangelogService } from 'src/app/modules/shared/services/changelog.service';
import { ProductDialogComponent, ProductDialogDataInterface } from '../product-dialog/product-dialog.component';
import { FullScreenDialogComponent, FullScreenDialogService } from '@headpower/components';
import { MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { PortalReportingService } from 'src/app/modules/shared/services/portal-reporting.service';

@Component({
    selector: 'app-changelog',
    templateUrl: './changelog.component.html',
    styleUrls: ['./changelog.component.scss'],
    standalone: false
})

export class ChangelogComponent implements OnInit, OnDestroy {
    public changelogEntries = {};
    public changelogView = [];
    public changelogSubscription: Subscription;
    public loading: boolean = true;
    public isChangelogPage: boolean = true;
    public products: PortalProduct[] = [];
    private readonly destroy$: Subject<void> = new Subject();
    public selectedProductId = 0;
    public selectedChangeLogId = 0;
    public productName: string = '';
    public firstLoadingDone: boolean = false;
    public localGroupPriority = GroupPriority;
    public nextLinks = {};

    length = 0;
    pageSize = 10;
    pageIndex = 0;
    pageEvent: PageEvent;
    pageStart = 1;
    pageEnd = 10;

    @Input() changelogEntriesInput = [];
    @Input() mobile;

    productDialogRef: MatDialogRef<FullScreenDialogComponent>;

    constructor(
        private readonly changelogService: ChangelogService,
        private readonly portalService: PortalService,
        private readonly portalReportingService: PortalReportingService,
        private readonly layoutBreakpointService: LayoutBreakpointService,
        private readonly fsDialogService: FullScreenDialogService,
        private readonly route: ActivatedRoute,
    ) {
        this.layoutBreakpointService.observer$
            .pipe(takeUntil(this.destroy$))
            .subscribe(result => this.mobile = result.handset);
    }

    ngOnInit() {
        let productId = this.route.snapshot.queryParamMap.get('productId');
        let changeLogId = this.route.snapshot.queryParamMap.get('changeLogId');

        if (!this.changelogEntriesInput.length) {
            if (productId != null && changeLogId != null) {
                this.selectedProductId = +productId;
                this.selectedChangeLogId = +changeLogId;
                this.changelogEntries[0] = [];
            }
            this.fetchProducts();
            this.getChangelogEntries();
        } else {
            this.changelogEntries[0] = this.changelogEntriesInput;
            this.length = this.changelogEntriesInput.length;
            this.isChangelogPage = false;
            this.getChangelogView();
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public getChangelogEntries() {
        if (this.changelogSubscription) {
            this.changelogSubscription.unsubscribe();
        }

        if (this.changelogEntries[this.selectedProductId]) {
            this.getChangelogView();
        } else {
            this.loading = true;
            this.changelogEntries[this.selectedProductId] = [];

            if (this.selectedProductId) {
                this.changelogSubscription = this.changelogService
                    .getChangelogEntries(undefined, (this.selectedProductId?.toString()) || undefined)
                    .subscribe((changelogEntries: any) => {
                        if (this.selectedChangeLogId == 0) {
                            this.changelogEntries[this.selectedProductId] = changelogEntries.data;
                        } else {
                            this.changelogEntries[this.selectedProductId] = changelogEntries.data.filter(log => log.attributes.drupal_internal__id == this.selectedChangeLogId);
                        }

                        this.nextLinks[this.selectedProductId] = changelogEntries.links.next;
                        this.length = this.changelogEntries[this.selectedProductId].length;
                        this.getChangelogView();
                    });
            } else {
                this.getChangelogView();
            }

        }
    }

    public getMoreChangelogEntries() {
        if (this.length === this.pageEnd) {
            this.loading = true;
            this.changelogService.getChangelogEntriesByUrl(this.nextLinks[this.selectedProductId].href).subscribe(res => {
                this.changelogEntries[this.selectedProductId] = [...this.changelogEntries[this.selectedProductId], ...res.data];
                this.nextLinks[this.selectedProductId] = res.links.next;
                this.handlePageEvent('next');
            })
        }
    }

    public handlePageEvent(type) {
        this.length = this.changelogEntries[this.selectedProductId].length;

        if (type === 'next') {
            this.pageIndex++
        } else if (type === 'previous') {
            this.pageIndex--
        }

        window.scrollTo(0, 0);
        this.getChangelogView();
    }

    updatePagination() {
        this.pageStart = this.pageSize * this.pageIndex + 1;
        this.pageEnd = this.pageSize * (this.pageIndex + 1);
        this.pageEnd = Math.min(this.pageEnd, this.length);
    }

    private getChangelogView() {
        this.length = this.changelogEntries[this.selectedProductId].length;
        this.updatePagination();
        const end = (this.pageIndex + 1) * this.pageSize;
        const start = this.pageIndex * this.pageSize;
        const part = this.changelogEntries[this.selectedProductId].slice(start, end);
        this.changelogView = part;
        if (this.selectedProductId !== 0 || !this.isChangelogPage) {
            this.loading = false;
        }
    }

    private fetchProducts() {
        this.portalService.getApplications().subscribe((res) => {
            this.products = res;
            this.firstLoadingDone = true;
            this.loading = false;

            if (this.isChangelogPage && this.mobile) {
                this.mobileProducts('HEADPOWER');
            }
        });
    }

    selectItem(item) {
        const previousProduct = this.selectedProductId;
        this.selectedProductId = item ? item.Access_UID : 0;
        this.productName = item ? item.AccessName : '';
        this.pageIndex = 0;

        // Reset possible single change log filtering
        if (this.selectedChangeLogId > 0) {
            this.selectedChangeLogId = 0;
            delete this.changelogEntries[previousProduct];
        }

        // log used tags
        this.portalReportingService.addAction(
            'Changelog product selected',
            this.selectedProductId.toString()
        ).subscribe({
            error: () => console.error('Error adding action')
        });

        this.getChangelogEntries();

        // update product dialog
        this.updateProductDialog();
    }

    public mobileProducts(type) {
        const originalSlectedProductId = this.selectedProductId;

        const emitProductSelect = new EventEmitter<PortalProduct>();
        emitProductSelect
            .pipe(takeUntil(this.destroy$))
            .subscribe(product => {
                this.selectItem(product);
            });

        let title = type === 'SUPPLIER' ? 'default.companysInstructions' : 'default.filterProducts';
        let group = type === 'SUPPLIER' ? GroupPriority.SUPPLIER : GroupPriority.HEADPOWER;

        this.productDialogRef = this.fsDialogService.open(ProductDialogComponent, {
            autoFocus: false,
            data: {
                dialogTitle: title,
                actionLabel: 'default.viewChanges',
                products: [...this.products],
                selectedProductId: this.selectedProductId,
                GroupPriority: group,
                emitProductSelect
            } as ProductDialogDataInterface,
        });
        this.productDialogRef.afterClosed().subscribe((confirmed = false) => {
            if (!confirmed) {
                // revert to original state
                const originalProduct = this.products.find(
                    (p) => p.Access_UID === originalSlectedProductId,
                );
                this.selectItem(originalProduct);
            }
        });
    }

    private updateProductDialog() {
        if (this.productDialogRef?.componentInstance) {
            const dialogData = (this.productDialogRef.componentInstance as any)
                .dialogData;
            dialogData.selectedProductId = this.selectedProductId;
        }
    }
}
